import { useQuery } from '@tanstack/react-query';

import { apiRequest } from 'helpers/api';
import { logException } from 'helpers/loggers';
import { useAuth } from 'hooks/useAuth';

export const ONBOARDING_RESTAURANT_QUERY_KEY = 'onboardingRestaurant';

type CompanyRestaurantsResponse = {
  data: { id: string }[];
};

type OnboardingRestaurantOptions = {
  /** Off while the caller has no use for the lookup, so no request goes out. */
  isEnabled?: boolean;
};

/**
 * Resolves a company admin's one restaurant from `/me`, for the `/onboarding` short link. A company
 * with no restaurants, or with more than one, resolves no restaurant ID, which keeps the wizard
 * away. CN admins have no restaurant of their own, so the lookup skips them.
 */
export const useOnboardingRestaurant = ({ isEnabled = true }: OnboardingRestaurantOptions = {}) => {
  const { isInternalUser, user } = useAuth();
  const userId: string | undefined = user?.id;
  const companyId: string | null | undefined = user?.company_id;

  const { data: restaurantIds = [], isLoading } = useQuery<string[]>({
    queryKey: [ONBOARDING_RESTAURANT_QUERY_KEY, userId, companyId],
    queryFn: async () => {
      // two is enough to tell one restaurant from many
      const { data, error } = await apiRequest<CompanyRestaurantsResponse>({
        path: `company/${companyId}/restaurants?page_size=2`,
        customHost: import.meta.env.VITE_ADMIN_API_HOST as string,
      });

      if (error) {
        logException({
          message: 'Failed to fetch the onboarding restaurant',
          error,
          context: { companyId },
          tags: null,
        });
        throw error;
      }

      return data?.data.map(restaurant => restaurant.id) ?? [];
    },
    enabled: isEnabled && Boolean(userId && companyId && !isInternalUser),
    staleTime: Infinity,
  });

  return {
    isLoading,
    restaurantId: restaurantIds.length === 1 ? restaurantIds[0] : undefined,
  };
};
